import React, { useMemo } from "react";
import { Image } from "primereact/image";
import img1 from "../../../assets/images/temp3.svg";
import { useEffect, useState } from "react";
import AtkLoader from "../../../AtlaskitControls/AtkLoader";
import { InputTextarea } from "primereact/inputtextarea";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { BlockNoteView } from "@blocknote/mantine";
import { useCreateBlockNote } from "@blocknote/react";
import { defaultBlockSpecs, BlockNoteSchema } from "@blocknote/core";
import Base64Image from "./Base64Image";
interface ProposalBreakdownProps {
  onChange: (markdown: string) => void;
}
export default function ProposalBreakdown(props: any, { onChange }: ProposalBreakdownProps) {
  const [policyData, setPolicyData] = useState<any>("");
  const [coverageData, setCoverageData] = useState<any>("");
  const [assetsData, setAssetsData] = useState<any>("");
  const [editingIndex, setEditingIndex] = useState<any>(null);
  const [editingSection, setEditingSection] = useState<any>(null);
  const [isProposalBreakdownEditing, setIsProposalBreakdownEditing] = useState(false);
  const [isPolicyDetailsEditing, setIsPolicyDetailsEditing] = useState(false);
  const [isCoverageDetailsEditing, setIsCoverageDetailsEditing] = useState(false);
  const [isAssetDetailsEditing, setIsAssetDetailsEditing] = useState(false);
  const [endorsementData, setEndorsementData] = useState<any>("");
  const [isEndorsementDetailsEditing, setIsEndorsementDetailsEditing] = useState(false);
  const filteredBlockSpecs = useMemo(() => {
    const { image, video, file, audio, ...rest } = defaultBlockSpecs;
    return rest;
  }, []);
  const schema = useMemo(
    () =>
      BlockNoteSchema.create({
        blockSpecs: filteredBlockSpecs,
      }),
    [filteredBlockSpecs]
  );
  const editor = useCreateBlockNote({ schema });
  useEffect(() => {
    if (props.policyDetails && props.coverageDetails && props.assetsInformation && props.endorsementsExclusions) {
      setPolicyData(props.policyDetails || {});
      setCoverageData(props.coverageDetails || {});
      setAssetsData(props.assetsInformation || {});
      setEndorsementData(props.endorsementsExclusions || {});
    }
  }, [props.policyDetails, props.coverageDetails, props.assetsInformation, props.endorsementsExclusions]);

  useEffect(() => {
    const loadInitialHTML = async () => {
      if (!editor || !props.policyDetails) return;

      const policy = props.policyDetails?.replace(/^"|"$/g, "").replace(/\\n/g, "\n") ?? "";

      setPolicyData(policy);

      try {
        const markdown = `${policy}`;
        const blocks = await editor?.tryParseHTMLToBlocks?.(markdown);

        if (Array.isArray(blocks) && blocks.length > 0) {
          await editor.replaceBlocks(editor.document, blocks);
        } else {
          console.warn("No valid blocks returned from tryParseHTMLToBlocks");
        }
      } catch (err) {
        console.error("Error parsing markdown to blocks or replacing blocks:", err);
      }
    };

    if (isPolicyDetailsEditing) {
      loadInitialHTML();
    }
  }, [isPolicyDetailsEditing, editor, props.policyDetails]);
  useEffect(() => {
    const container = document.querySelector(".quote-proposal-table");

    if (container) {
      const textColorElements = document.querySelectorAll("[data-text-color]");
      textColorElements.forEach((el) => {
        const color = el.getAttribute("data-text-color");
        if (color) {
          (el as HTMLElement).style.color = color;
        }
      });

      const bgColorElements = document.querySelectorAll("[data-background-color]");
      bgColorElements.forEach((el) => {
        const color = el.getAttribute("data-background-color");
        if (color) {
          (el as HTMLElement).style.backgroundColor = color;
        }
      });
    }
  }, [props.policyDetails, policyData]);

  useEffect(() => {
    const loadInitialHTML = async () => {
      if (!editor || !props.coverageDetails) return;

      const coverage = props.coverageDetails?.replace(/^"|"$/g, "").replace(/\\n/g, "\n") ?? "";

      setCoverageData(coverage);

      try {
        const markdown = `${coverage}`;
        const blocks = await editor?.tryParseMarkdownToBlocks(markdown);

        if (blocks && Array.isArray(blocks)) {
          await editor.replaceBlocks(editor.document, blocks);
        } else {
          console.warn("Invalid blocks returned from tryParseMarkdownToBlocks");
        }
      } catch (err) {
        console.error("Error parsing markdown or replacing blocks:", err);
      }
    };

    if (isCoverageDetailsEditing) {
      loadInitialHTML();
    }
  }, [isCoverageDetailsEditing, editor, props.coverageDetails]);

  useEffect(() => {
    const loadInitialHTML = async () => {
      if (!editor || !props.assetsInformation) return;

      const assets = props.assetsInformation?.replace(/^"|"$/g, "").replace(/\\n/g, "\n") ?? "";

      setAssetsData(assets);

      try {
        const markdown = `${assets}`;
        const blocks = await editor?.tryParseMarkdownToBlocks?.(markdown);

        if (Array.isArray(blocks) && blocks.length > 0) {
          await editor.replaceBlocks(editor.document, blocks);
        } else {
          console.warn("No valid blocks returned from tryParseMarkdownToBlocks");
        }
      } catch (err) {
        console.error("Error parsing markdown to blocks or replacing blocks:", err);
      }
    };

    if (isAssetDetailsEditing) {
      loadInitialHTML();
    }
  }, [isAssetDetailsEditing, editor, props.assetsInformation]);

  useEffect(() => {
    const loadInitialHTML = async () => {
      if (!editor || !props.endorsementsExclusions) return;

      const endorsement = props.endorsementsExclusions?.replace(/^"|"$/g, "").replace(/\\n/g, "\n") ?? "";

      setEndorsementData(endorsement);

      try {
        const markdown = `${endorsement}`;
        const blocks = await editor?.tryParseMarkdownToBlocks?.(markdown);

        if (Array.isArray(blocks) && blocks.length > 0) {
          await editor.replaceBlocks(editor.document, blocks);
        } else {
          console.warn("No valid blocks returned from tryParseMarkdownToBlocks");
        }
      } catch (err) {
        console.error("Error parsing markdown to blocks or replacing blocks:", err);
      }
    };

    if (isEndorsementDetailsEditing) {
      loadInitialHTML();
    }
  }, [isEndorsementDetailsEditing, editor, props.endorsementsExclusions]);

  const onPolicyDetailsChange = async () => {
    const markdown = await editor.blocksToHTMLLossy(editor.document);
    if (onChange) {
      onChange(markdown);
    }
    setPolicyData(markdown);
  };
  const onCoverageDetailsChange = async () => {
    const markdown = await editor.blocksToMarkdownLossy(editor.document);
    if (onChange) {
      onChange(markdown);
    }
    setCoverageData(markdown);
  };
  const onAssetTypeDetailsChange = async () => {
    const markdown = await editor.blocksToMarkdownLossy(editor.document);
    if (onChange) {
      onChange(markdown);
    }
    setAssetsData(markdown);
  };
  const onEndorsementDetailsChange = async () => {
    const markdown = await editor.blocksToMarkdownLossy(editor.document);
    if (onChange) {
      onChange(markdown);
    }
    setEndorsementData(markdown);
  };

  return (
    <div className="container p-0 border">
      <div className="w-100">
        <div className="row mx-auto px-2 ">
          <div className="col-12" style={{ borderBottom: `1px solid ${props.primaryColor}` }}>
            <div className="row d-flex align-items-end">
              <div className="col-lg-8 col-md-8 col-sm-8 col-10">
                <div className="d-flex align-items-center ">
                  <h4 className="fw-bold " style={{ color: `${props.primaryColor}` }}>
                    Proposal Breakdown{" "}
                    {props.isEditable && !props.isGeneratingPdf ? (
                      <>
                        {!isProposalBreakdownEditing ? (
                          <i className="fa fa-pencil text-12 ms-2 show-cursor" title="Edit" onClick={() => setIsProposalBreakdownEditing(true)} />
                        ) : (
                          <span className="text-12 ">
                            {isProposalBreakdownEditing && props.saveLoading ? (
                              <div className="d-inline-flex align-items-center">
                                <AtkLoader />
                              </div>
                            ) : (
                              <i
                                className="fa fa-check mx-2 show-cursor text-success"
                                title="Save"
                                onClick={() => {
                                  setIsProposalBreakdownEditing(false);
                                  props.onTemplateDetailsSave();
                                }}
                              />
                            )}
                            <i
                              className="fa fa-times show-cursor text-danger"
                              title="Cancel"
                              onClick={() => {
                                setIsProposalBreakdownEditing(false);
                                props.onCancelTemplateDetailsToSave();
                              }}
                            />
                          </span>
                        )}
                      </>
                    ) : null}
                  </h4>
                </div>
              </div>
              <div className="col-lg-4 col-md-4 col-sm-4 col-2" style={{ backgroundColor: `${props.primaryColor}`, paddingTop: "5rem" }}></div>
            </div>
          </div>

          <div className="col-12 ">
            {isProposalBreakdownEditing ? (
              <InputTextarea
                value={props.proposalBreakdownDescription}
                onChange={props.onproposalBreakdownDescriptionChange}
                autoResize
                className="text-12 w-100 mt-1"
              />
            ) : (
              <p className="small mb-1 mt-2">{props.proposalBreakdownDescription}</p>
            )}
          </div>

          <div className="col-12 mb-2 ">
            {!isPolicyDetailsEditing ? (
              <>
                {props.isEditable && !props.isGeneratingPdf ? (
                  <>
                    <i
                      className="fa fa-pencil text-12 ms-2 mb-2 show-cursor d-flex flex-row justify-content-end"
                      title="Edit"
                      onClick={() => setIsPolicyDetailsEditing(true)}
                    />
                  </>
                ) : null}
                {/* <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} className="markdown-table quote-proposal-table  mt-3">
                  {policyData.replace(/^"|"$/g, "").replace(/\\n/g, "\n")}
                </ReactMarkdown> */}
                <div
                  className="markdown-table quote-proposal-table mt-3"
                  dangerouslySetInnerHTML={{
                    __html: policyData.replace(/^"|"$/g, "").replace(/\\n/g, "<br />"),
                  }}
                />
              </>
            ) : (
              <>
                <span className="text-12 mb-2 d-flex flex-row justify-content-end ">
                  {isPolicyDetailsEditing && props.saveLoading ? (
                    <div className="d-inline-flex align-items-center">
                      <AtkLoader />
                    </div>
                  ) : (
                    <i
                      className="fa fa-check mx-2 show-cursor text-success "
                      title="Save"
                      onClick={() => {
                        setIsPolicyDetailsEditing(false);
                        props.onTemplateDetailsSave(policyData, coverageData, assetsData, endorsementData);
                      }}
                    />
                  )}
                  <i
                    className="fa fa-times show-cursor text-danger"
                    title="Cancel"
                    onClick={() => {
                      setIsPolicyDetailsEditing(false);
                    }}
                  />
                </span>
                <div className="markdown-table ">
                  <BlockNoteView
                    editor={editor}
                    onChange={onPolicyDetailsChange}

                    // editable={true}
                  />
                </div>
              </>
            )}
          </div>
          <div className="col-12 mb-2 ">
            {!isCoverageDetailsEditing ? (
              <>
                {props.isEditable && !props.isGeneratingPdf ? (
                  <>
                    <i
                      className="fa fa-pencil text-12 ms-2 mb-2 show-cursor d-flex flex-row justify-content-end"
                      title="Edit"
                      onClick={() => setIsCoverageDetailsEditing(true)}
                    />
                  </>
                ) : null}
                <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} className="markdown-table quote-proposal-table mt-3">
                  {coverageData.replace(/^"|"$/g, "").replace(/\\n/g, "\n")}
                </ReactMarkdown>
              </>
            ) : (
              <>
                <span className="text-12 mb-2  d-flex flex-row justify-content-end ">
                  {isCoverageDetailsEditing && props.saveLoading ? (
                    <div className="d-inline-flex align-items-center">
                      <AtkLoader />
                    </div>
                  ) : (
                    <i
                      className="fa fa-check mx-2 show-cursor text-success "
                      title="Save"
                      onClick={() => {
                        setIsCoverageDetailsEditing(false);
                        props.onTemplateDetailsSave(policyData, coverageData, assetsData, endorsementData);
                      }}
                    />
                  )}
                  <i
                    className="fa fa-times show-cursor text-danger"
                    title="Cancel"
                    onClick={() => {
                      setIsCoverageDetailsEditing(false);
                    }}
                  />
                </span>
                <div className="markdown-table ">
                  <BlockNoteView
                    editor={editor}
                    onChange={onCoverageDetailsChange}
                    // editable={true}
                  />
                </div>
              </>
            )}
          </div>

          <div className="col-12 mb-2 ">
            {!isAssetDetailsEditing ? (
              <>
                {props.isEditable && !props.isGeneratingPdf ? (
                  <>
                    <i
                      className="fa fa-pencil text-12 ms-2 mb-2 show-cursor d-flex flex-row justify-content-end"
                      title="Edit"
                      onClick={() => setIsAssetDetailsEditing(true)}
                    />
                  </>
                ) : null}
                <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} className="markdown-table quote-proposal-table mt-3">
                  {assetsData.replace(/^"|"$/g, "").replace(/\\n/g, "\n")}
                </ReactMarkdown>
              </>
            ) : (
              <>
                <span className="text-12 mb-2  d-flex flex-row justify-content-end ">
                  {isAssetDetailsEditing && props.saveLoading ? (
                    <div className="d-inline-flex align-items-center">
                      <AtkLoader />
                    </div>
                  ) : (
                    <i
                      className="fa fa-check mx-2 show-cursor text-success "
                      title="Save"
                      onClick={() => {
                        setIsAssetDetailsEditing(false);
                        props.onTemplateDetailsSave(policyData, coverageData, assetsData, endorsementData);
                      }}
                    />
                  )}
                  <i
                    className="fa fa-times show-cursor text-danger"
                    title="Cancel"
                    onClick={() => {
                      setIsAssetDetailsEditing(false);
                    }}
                  />
                </span>
                <div className="markdown-table ">
                  <BlockNoteView editor={editor} onChange={onAssetTypeDetailsChange} />
                </div>
              </>
            )}
          </div>

          <div className="col-12 mb-2 ">
            {!isEndorsementDetailsEditing ? (
              <>
                {props.isEditable && !props.isGeneratingPdf ? (
                  <>
                    <i
                      className="fa fa-pencil text-12 ms-2 mb-2 show-cursor d-flex flex-row justify-content-end"
                      title="Edit"
                      onClick={() => setIsEndorsementDetailsEditing(true)}
                    />
                  </>
                ) : null}
                <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]} className="markdown-table quote-proposal-table mt-3">
                  {endorsementData.replace(/^"|"$/g, "").replace(/\\n/g, "\n")}
                </ReactMarkdown>
              </>
            ) : (
              <>
                <span className="text-12 mb-2  d-flex flex-row justify-content-end ">
                  {isEndorsementDetailsEditing && props.saveLoading ? (
                    <div className="d-inline-flex align-items-center">
                      <AtkLoader />
                    </div>
                  ) : (
                    <i
                      className="fa fa-check mx-2 show-cursor text-success "
                      title="Save"
                      onClick={() => {
                        setIsEndorsementDetailsEditing(false);
                        props.onTemplateDetailsSave(policyData, coverageData, assetsData, endorsementData);
                      }}
                    />
                  )}
                  <i
                    className="fa fa-times show-cursor text-danger"
                    title="Cancel"
                    onClick={() => {
                      setIsEndorsementDetailsEditing(false);
                    }}
                  />
                </span>
                <div className="markdown-table ">
                  <BlockNoteView editor={editor} onChange={onEndorsementDetailsChange} />
                </div>
              </>
            )}
          </div>

          <div className="col-12 text-center ">
            <Base64Image imageUrl={props.templateImages?.split(",")[1] || ""} className="rounded quote-template-img" alt="Insurance Image" />
          </div>
        </div>

        <div
          className="position-relative text-center text-light small mt-3 border-top p-4 w-100"
          style={{ backgroundColor: `${props.primaryColor}`, color: "#fff" }}
        >
          <div
            className="position-absolute"
            style={{ width: "70%", height: "14px", top: "0px", left: "15%", backgroundColor: `${props.secondaryColor}` }}
          ></div>
        </div>
      </div>
    </div>
  );
}
